import type { GalleryItem } from '@ecowheels/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card, ConfirmButton, PageHeader, QueryState } from '@/components/ui';

const readAsBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

export function GalleryPage() {
  const queryClient = useQueryClient();
  const [order, setOrder] = useState<GalleryItem[]>([]);
  const [dragging, setDragging] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ['gallery'],
    queryFn: () => api.get<GalleryItem[]>('/admin/gallery'),
  });

  // Local copy so a drag reorders instantly; the server is told once, on drop.
  useEffect(() => {
    if (data) setOrder(data);
  }, [data]);

  const save = useMutation({
    // `orderAll` takes the ids in their new order and sets priority = index —
    // one request for the whole list. The alternative endpoint (PATCH
    // changePriority) swaps *filenames* between rows one step at a time, which
    // is both N requests for a drag and a strange way to reorder.
    mutationFn: (ids: number[]) => api.post('/admin/gallery/order-all', ids),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gallery'] }),
    onError: () => {
      setError('A sorrend mentése nem sikerült.');
      queryClient.invalidateQueries({ queryKey: ['gallery'] });
    },
  });

  const remove = useMutation({
    // Despite the route param being called `priority`, the service looks the
    // row up by `id`. Passing a priority here deletes the wrong image.
    mutationFn: (id: number) => api.delete(`/admin/gallery/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gallery'] }),
    onError: () => setError('A törlés nem sikerült.'),
  });

  const add = useMutation({
    mutationFn: async (files: File[]) => {
      for (const file of files) {
        const result = await api.post<number | GalleryItem>('/admin/gallery', {
          name: file.name,
          base64: await readAsBase64(file),
          start: false,
        });
        // addImage returns -1 rather than an error status when the filename is
        // already taken, so a duplicate would otherwise look like a success.
        if (result === -1) throw new Error(`Ilyen nevű kép már létezik: ${file.name}`);
      }
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['gallery'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  function drop(targetIndex: number) {
    if (dragging === null || dragging === targetIndex) return;
    const next = [...order];
    const [moved] = next.splice(dragging, 1);
    if (moved) next.splice(targetIndex, 0, moved);
    setOrder(next);
    setDragging(null);
    save.mutate(next.map((item) => item.id));
  }

  return (
    <>
      <PageHeader
        title="Galéria"
        action={
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/*"
              multiple
              className="sr-only"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                e.target.value = '';
                if (files.length) add.mutate(files);
              }}
            />
            <span className="inline-flex items-center rounded-md bg-brand px-4 py-2 text-sm font-medium text-ink hover:opacity-90">
              {add.isPending ? 'Feltöltés…' : 'Kép hozzáadása'}
            </span>
          </label>
        }
      />

      {error && <p className="mb-4 text-sm text-red-400">{error}</p>}

      <QueryState
        isLoading={isLoading}
        error={loadError}
        isEmpty={order.length === 0}
        emptyText="Még nincs kép a galériában."
      >
        <p className="mb-3 text-xs text-white/40">
          Húzza a képeket a sorrend módosításához. A sorrend a nyilvános oldalon is ez lesz.
        </p>

        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {order.map((item, index) => (
            <li
              key={item.id}
              draggable
              onDragStart={() => setDragging(index)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => drop(index)}
              className={`cursor-grab active:cursor-grabbing ${dragging === index ? 'opacity-40' : ''}`}
            >
              <Card className="overflow-hidden">
                <img
                  src={item.url}
                  alt={item.filename}
                  className="h-36 w-full bg-white/5 object-cover"
                  loading="lazy"
                />
                <div className="flex items-center justify-between gap-2 p-2">
                  <span className="truncate text-xs text-white/50" title={item.filename}>
                    {index + 1}. {item.filename}
                  </span>
                  <ConfirmButton
                    onConfirm={() => remove.mutate(item.id)}
                    confirmText={`Biztosan törli a(z) "${item.filename}" képet?`}
                    className="!px-2 !py-1"
                  >
                    ×
                  </ConfirmButton>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      </QueryState>
    </>
  );
}
